import { useEffect, useState } from 'react';
import { ApiError, get, leerSesion, leerToken, olvidarToken } from './api';
import type { Contexto, Sesion } from './tipos';
import { Entrada } from './pages/Entrada';
import { Plan } from './pages/Plan';
import { Admin } from './pages/Admin';
import { Matricula } from './components/ui';

/**
 * Qué se sirve en esta dirección.
 *
 * El panel cuelga de `admin.`, y cualquier otro subdominio es el MRP de un
 * cliente. Se mira el nombre del host y no un parámetro que mande el
 * navegador: el cliente no puede ser algo que elija quien abre la página.
 */
function esPanel(): boolean {
  const host = window.location.hostname;
  if (host.startsWith('admin.')) return true;
  // En desarrollo se entra por localhost. El panel se abre con ?admin.
  return new URLSearchParams(window.location.search).has('admin');
}

export function App(): JSX.Element {
  const panel = esPanel();
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [contexto, setContexto] = useState<Contexto | null>(null);
  const [sociedad, setSociedad] = useState<string>('');
  const [cargando, setCargando] = useState(true);
  const [tema, setTema] = useState<'auto' | 'light' | 'dark'>('auto');

  useEffect(() => {
    if (tema === 'auto') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', tema);
  }, [tema]);

  // Con un token guardado se intenta seguir donde se estaba. Si ha caducado,
  // la API devuelve 401 y se vuelve a la entrada sin dejar la pantalla a medias.
  useEffect(() => {
    if (!leerToken()) {
      setCargando(false);
      return;
    }
    if (panel) {
      // Quien manda es el token; esto solo recupera el nombre para la barra.
      setSesion(leerSesion<Sesion>() ?? { scope: 'admin', username: 'admin', name: 'ECINSA', role: 'admin' });
      setCargando(false);
      return;
    }
    void get<Contexto>('/context')
      .then((c) => {
        setContexto(c);
        setSesion(c.session);
        setSociedad(c.companies.find((x) => x.isDefault)?.companyDb ?? c.companies[0]?.companyDb ?? '');
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) olvidarToken();
      })
      .finally(() => setCargando(false));
  }, [panel]);

  async function trasEntrar(s: Sesion): Promise<void> {
    setSesion(s);
    if (panel) return;
    const c = await get<Contexto>('/context');
    setContexto(c);
    setSociedad(c.companies.find((x) => x.isDefault)?.companyDb ?? c.companies[0]?.companyDb ?? '');
  }

  if (cargando) return <div className="entrada">Cargando…</div>;
  if (!sesion) return <Entrada admin={panel} onEntrar={(s) => void trasEntrar(s)} />;

  const activa = contexto?.companies.find((c) => c.companyDb === sociedad) ?? null;

  return (
    <div className="app">
      <header className="barra">
        <div className="marca">
          MRP <span>· {panel ? 'administración' : (contexto?.tenant.name ?? 'ECINSA')}</span>
        </div>
        {!panel && activa ? <Matricula sociedad={activa} /> : null}
        {!panel && contexto ? (
          <span className="sutil">
            túnel <strong>{contexto.tenant.hubClient}</strong>
          </span>
        ) : null}

        <div className="derecha">
          <select value={tema} onChange={(e) => setTema(e.target.value as typeof tema)} aria-label="Tema" style={{ padding: '4px 8px' }}>
            <option value="auto">Tema del sistema</option>
            <option value="light">Claro</option>
            <option value="dark">Oscuro</option>
          </select>
          <span className="sutil">{sesion.name}</span>
          <button
            className="secundario"
            onClick={() => {
              olvidarToken();
              setSesion(null);
              setContexto(null);
            }}
          >
            Salir
          </button>
        </div>
      </header>

      <main>
        {panel ? (
          <Admin />
        ) : contexto ? (
          <Plan contexto={contexto} sociedad={sociedad} onCambiarSociedad={setSociedad} />
        ) : (
          <div className="tarjeta">Este cliente no tiene ninguna sociedad dada de alta. Se añade en el panel de administración.</div>
        )}
      </main>
    </div>
  );
}
